import React, { useEffect, useMemo, useState } from "react";
import { api, fmtError, eur, NIVEAU_STYLES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Calculator, Save } from "lucide-react";
import { toast } from "sonner";

export default function Calculators() {
  const [catalog, setCatalog] = useState([]);
  const [sites, setSites] = useState([]);
  const [cat, setCat] = useState("");
  const [selected, setSelected] = useState(null);
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [prixUnitaire, setPrixUnitaire] = useState("");
  const [siteId, setSiteId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/calculations/catalog").then((r) => {
      setCatalog(r.data);
      if (r.data.length) setCat(r.data[0].categorie);
    }).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => [...new Set(catalog.map((c) => c.categorie))], [catalog]);
  const calcs = catalog.filter((c) => c.categorie === cat);

  const select = (c) => {
    setSelected(c);
    const init = {};
    c.champs.forEach((f) => { init[f.k] = f.d ?? ""; });
    setInputs(init);
    setResult(null);
    setError("");
  };

  const run = async (enregistrer = false) => {
    if (!selected) return;
    setError("");
    try {
      const { data } = await api.post("/calculations", {
        type: selected.type, inputs, enregistrer,
        site_id: enregistrer ? siteId || null : null,
        prix_unitaire: prixUnitaire ? parseFloat(prixUnitaire) : null,
      });
      setResult(data);
      if (enregistrer) toast.success("Calcul enregistré dans le chantier");
    } catch (e) {
      setError(fmtError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="calculators-page">
      <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Calculs BTP / TP</h1>

      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="calc-categories">
        {categories.map((c) => (
          <button key={c} data-testid={`calc-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => { setCat(c); setSelected(null); setResult(null); }}
            className={`px-4 py-2.5 rounded-xl font-bold text-sm whitespace-nowrap border-2 touch-target ${cat === c ? "bg-amber-500 text-slate-900 border-amber-500" : "bg-card border-border"}`}>
            {c}
          </button>
        ))}
      </div>

      {!selected ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="calc-list">
          {calcs.map((c) => (
            <button key={c.type} data-testid={`calc-${c.type}`} onClick={() => select(c)}
              className="flex items-center gap-3 p-4 rounded-xl bg-card border-2 border-border hover:border-amber-500 hover:shadow-md transition-all text-left touch-target">
              <Calculator className="h-6 w-6 text-amber-600 shrink-0" />
              <span className="font-semibold text-sm">{c.label}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-card rounded-xl border border-border p-5 space-y-4" data-testid="calc-form">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold uppercase">{selected.label}</h2>
              <button data-testid="calc-back" onClick={() => { setSelected(null); setResult(null); }} className="text-sm font-bold text-amber-600">← Retour</button>
            </div>
            {selected.champs.map((f) => (
              <div key={f.k}>
                <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{f.l}</label>
                <Input data-testid={`input-${f.k}`} type={f.t === "text" ? "text" : "number"} step="any"
                  value={inputs[f.k] ?? ""} onChange={(e) => setInputs({ ...inputs, [f.k]: e.target.value })}
                  className="mt-1 py-3.5 text-base" />
              </div>
            ))}
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Coût unitaire (optionnel, €)</label>
              <Input data-testid="input-prix-unitaire" type="number" step="any" value={prixUnitaire}
                onChange={(e) => setPrixUnitaire(e.target.value)} className="mt-1 py-3.5" />
            </div>
            {error && <div data-testid="calc-error" className="text-sm font-semibold text-red-600">{error}</div>}
            <Button data-testid="calc-run" onClick={() => run(false)} className="w-full h-14 text-base font-bold bg-amber-500 hover:bg-amber-400 text-slate-900">
              CALCULER
            </Button>
          </div>

          {result && (
            <div className={`rounded-xl border-2 p-5 space-y-3 ${NIVEAU_STYLES[result.niveau] || NIVEAU_STYLES.VERT}`} data-testid="calc-result">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold uppercase tracking-widest">Niveau {result.niveau}</span>
                <span className="text-xs font-semibold">{result.categorie}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-bold uppercase opacity-70">Résultat brut</div>
                  <div className="font-mono-num text-2xl font-bold" data-testid="result-brut">{result.resultat_brut ?? "—"} {result.unite}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase opacity-70">Quantité recommandée</div>
                  <div className="font-mono-num text-2xl font-bold" data-testid="result-recommande">{result.quantite_recommandee ?? "—"} {result.unite}</div>
                </div>
              </div>
              <div className="text-sm font-semibold" data-testid="result-formule">📐 {result.formule}</div>
              {result.details && (
                <div className="bg-white/60 dark:bg-black/20 rounded-lg p-3 text-sm space-y-1" data-testid="result-details">
                  {Object.entries(result.details).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span className="opacity-80">{k.replace(/_/g, " ")}</span><span className="font-mono-num font-bold">{v}</span></div>
                  ))}
                </div>
              )}
              {result.cout_total != null && (
                <div className="text-lg font-bold font-mono-num" data-testid="result-cout">Coût total : {eur(result.cout_total)}</div>
              )}
              {result.avertissement && <div className="text-sm font-bold" data-testid="result-warning">⚠ {result.avertissement}</div>}
              <div className="text-xs opacity-80 border-t border-current/20 pt-2">{result.avertissement_global}</div>
              <div className="flex gap-2 pt-1">
                <select data-testid="calc-site-select" value={siteId} onChange={(e) => setSiteId(e.target.value)}
                  className="flex-1 h-12 rounded-lg border border-border bg-card text-foreground px-3 text-sm font-semibold">
                  <option value="">— Lier à un chantier —</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.numero} — {s.nom}</option>)}
                </select>
                <Button data-testid="calc-save" onClick={() => run(true)} disabled={!siteId}
                  className="h-12 bg-slate-900 text-white hover:bg-slate-800 font-bold">
                  <Save className="h-4 w-4 mr-1" /> Enregistrer
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
